"use client";

import Modal from "../modal/Modal";
import { useSearchParams } from "next/navigation";
import { getDeviceTypeClientSide, updateSearchParams } from "~/utils/clientUtils";
import Flex from "../flex/Flex";
import { Icon, IconName } from "../icons/Icon";
import { colors, spacing } from "~/utils/tailwindUtils";
import Text from "../texts/Text";
import { useAtom } from "jotai";
import { openDarkBackgroundAtom } from "~/atoms";
import Button from "../buttons/Button";
import { MouseEvent, useState } from "react";
import DatePicker from "./DatePicker";
import Card from "../cards/Card";
import MobileNavPopup from "../modal/MobileNavPopop";
import { ColorModeEnum } from "~/utils/generalUtils";
import { useI18n } from "~/locales/client";
import { twMerge } from "tailwind-merge";
import { IS_DATE_PICKER_MODAL_OPEN } from "~/constants/queryParams";
import { formatDate } from "~/utils/dateUtils";

type DatePickerButtonProps = {
  value?: Date;
  firstAvailableDate?: Date;
  lastAvailableDate?: Date;
  onChange?: (date: Date) => void;
  onClear?: () => void;
  submitAction?: () => void;
  modalHeaderTitle?: string;
  modalHeaderBody?: string;
  labelText?: string;
  confirmButtonText?: string;
  classNames?: string;
  uniqueQueryId?: string;
  isDisabled?: boolean;
  showWarning?: boolean;
  placeholder?: string;
  allowPastDates?: boolean;
  allowClear?: boolean;
};

const isLoading = false;

/**
 * A functional component that renders the date picker button. It contains the date picker modal.
 * @param {DatePickerButtonProps} props The props for the component
 * @return {JSX.Element} The date picker button component
 */
const DatePickerButton = ({
  value,
  firstAvailableDate,
  lastAvailableDate,
  onChange,
  onClear,
  submitAction,
  modalHeaderTitle,
  modalHeaderBody,
  labelText,
  confirmButtonText,
  classNames,
  uniqueQueryId,
  isDisabled,
  showWarning,
  placeholder = "",
  allowPastDates,
  allowClear,
}: DatePickerButtonProps) => {
  const searchParamOpenModal = uniqueQueryId ? IS_DATE_PICKER_MODAL_OPEN + uniqueQueryId : IS_DATE_PICKER_MODAL_OPEN;
  const searchParams = useSearchParams();
  const isDatePickerOpen = Boolean(searchParams.get(searchParamOpenModal));
  const { isMobile } = getDeviceTypeClientSide();
  const translate = useI18n();
  const [, setIsOpenBackground] = useAtom(openDarkBackgroundAtom);
  const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);

  const handleCloseDatePicker = () => {
    const params = new URLSearchParams(searchParams);
    params.delete(searchParamOpenModal);
    updateSearchParams(params);
    setIsOpenBackground(false);
  };

  const handleSubmitClick = () => {
    handleCloseDatePicker();
    submitAction?.();
  };

  const handleChangeDate = (date: Date) => {
    if (isDisabled) return;

    setSelectedDate(date);
    onChange?.(date);
    handleCloseDatePicker();
  };

  const handleOpenDatePicker = () => {
    if (isDisabled) return;

    const params = new URLSearchParams(searchParams);
    params.set(searchParamOpenModal, "true");
    updateSearchParams(params);
    setIsOpenBackground(true);
  };

  const handleClearDate = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (isDisabled) return;

    setSelectedDate(null);
    onClear?.();
  };

  return (
    <>
      <Flex
        className={twMerge(
          "relative w-full md:min-w-32 bg-gray-50 gap-2 px-3 py-2 h-10 rounded-lg cursor-pointer border border-gray-300",
          isDisabled && "opacity-50 hover:bg-gray-50 cursor-not-allowed",
          allowClear && value && "md:min-w-38",
          classNames
        )}
        onClick={handleOpenDatePicker}
      >
        {showWarning && (
          <Flex className="items-center flex-1">
            <Icon name={IconName.AlertTriangle} color={colors.orange[500]} size={spacing[6]} />
          </Flex>
        )}
        <Flex className="flex-6 items-center">
          <Text
            color={placeholder && !labelText ? "lightGray" : "primary"}
            fontWeight="medium"
            className="md:leading-4"
          >
            {labelText || (value ? formatDate(value) : placeholder)}
          </Text>
        </Flex>
        <Flex className="items-center justify-end flex-1">
          {allowClear && value && (
            <button
              type="button"
              onClick={handleClearDate}
              className="mr-2 cursor-pointer rounded-full p-0.5 hover:bg-gray-300"
              aria-label={translate("general.clear")}
              title={translate("general.clear")}
            >
              <Icon name={IconName.Cancel} customStrokeWidth={2} size={spacing[4]} color={colors.black} />
            </button>
          )}
          <Icon name={IconName.Calendar} size={spacing[5]} color={colors.gray[500]} />
        </Flex>
      </Flex>
      {isMobile ? (
        <Card className="fixed bottom-0 left-0 w-screen rounded-b-none p-0 md:hidden gap-0 bg-transparent border-none z-90">
          <MobileNavPopup
            showMobileNavPopover={isDatePickerOpen}
            handleSwitchMobileNavPopover={handleCloseDatePicker}
            className="items-center h-[70dvh]"
          >
            <Icon
              name={IconName.Cancel}
              size={spacing[6]}
              colorMode={ColorModeEnum.Fill}
              className="justify-end pt-4"
              onClick={handleCloseDatePicker}
            />
            <Flex className="w-full flex-col">
              <Flex className="gap-x-4">
                <Icon name={IconName.Calendar} width={spacing[6]} />
                <Text fontWeight="semibold" className="text-lg">
                  {modalHeaderTitle}
                </Text>
              </Flex>
              <Text color="lightGray" className="text-xs pl-10">
                {modalHeaderBody}
              </Text>
            </Flex>
            <DatePicker
              firstAvailableDate={firstAvailableDate}
              lastAvailableDate={lastAvailableDate}
              selectedDate={selectedDate || value}
              onChange={handleChangeDate}
              allowPastDates={allowPastDates}
            />
            {submitAction && (
              <Flex className="justify-between gap-x-4 w-full pt-4">
                <Button
                  text={translate("general.cancel")}
                  type="button"
                  buttonClassName="w-24"
                  buttonStyle="default"
                  onClick={handleCloseDatePicker}
                />
                <Button
                  text={translate(confirmButtonText || "")}
                  type="submit"
                  buttonClassName="w-24"
                  isLoading={isLoading}
                  disabled={isLoading || !selectedDate}
                  onClick={handleSubmitClick}
                />
              </Flex>
            )}
          </MobileNavPopup>
        </Card>
      ) : (
        <Modal
          isOpen={isDatePickerOpen}
          closeModal={handleCloseDatePicker}
          modalClassname="bg-opacity-20"
          classname="lg:p-10 lg:pt-0 lg:w-120"
        >
          <Flex className="w-full flex-col">
            <Flex className="gap-x-4">
              <Icon name={IconName.Calendar} width={spacing[6]} />
              <Text fontWeight="semibold" className="text-lg">
                {modalHeaderTitle}
              </Text>
            </Flex>
            <Text color="lightGray" className="text-xs pl-10">
              {modalHeaderBody}
            </Text>
          </Flex>
          <DatePicker
            firstAvailableDate={firstAvailableDate}
            lastAvailableDate={lastAvailableDate}
            selectedDate={selectedDate || value}
            onChange={handleChangeDate}
            allowPastDates={allowPastDates}
          />
          {Boolean(submitAction) && (
            <Flex className="justify-between gap-x-4 w-full">
              <Button
                text={translate("general.cancel")}
                type="button"
                buttonClassName="w-24 bg-white border-gray-300"
                buttonStyle="default"
                onClick={handleCloseDatePicker}
              />
              <Button
                text={translate(confirmButtonText || "")}
                type="submit"
                buttonClassName="w-24"
                isLoading={isLoading}
                disabled={isLoading || !selectedDate}
                onClick={handleSubmitClick}
              />
            </Flex>
          )}
        </Modal>
      )}
    </>
  );
};

export default DatePickerButton;
